<template>
  <div class="shiori-view">
    <AppHeader />

    <!-- しおりヘッダー -->
    <div class="shiori-header trip-dark-header" v-if="shioriData" :style="darkHeaderOverrideStyle">
      <div class="container">
        <!-- 1行目: 戻るリンク + アクションボタン -->
        <div class="shiori-header-top">
          <router-link
            to="/mypage"
            class="back-link"
            aria-label="しおり一覧に戻る"
          >
            ← 旅行一覧
          </router-link>
          <div class="header-actions">
            <button v-if="canWrite" class="btn-header-action desktop-header-action" @click="openEditModal">
              編集
            </button>
            <button v-if="isAuthenticated && canManage" class="btn-header-action desktop-header-action" @click="openShareModal">
              共有
            </button>
            <button class="btn-header-action desktop-header-action" @click="copyShiori">
              複製
            </button>
            <div class="shiori-action-menu" @click.stop>
              <button
                class="shiori-kebab-button btn-header-action"
                @click="isActionMenuOpen = !isActionMenuOpen"
                aria-label="しおりメニュー"
              >
                ⋮
              </button>
              <div
                v-if="isActionMenuOpen"
                class="shiori-action-dropdown"
              >
                <button
                  v-if="isAuthenticated"
                  class="shiori-action-item"
                  :disabled="isExportingPdf"
                  @click="runShioriAction(saveShioriPdf)"
                >
                  {{ isExportingPdf ? "PDF作成中..." : "PDF保存" }}
                </button>
                <button
                  v-if="isOwner"
                  class="shiori-action-item shiori-action-delete"
                  @click="runShioriAction(deleteShiori)"
                >
                  しおりを削除
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- 2行目: タイトル + ステータス -->
        <div class="shiori-title-row">
          <h1 class="trip-title">{{ shioriData.SUBJECT_NMV }}</h1>
          <span v-if="!canWrite" class="trip-status-badge">閲覧のみ</span>
          <span v-else-if="tripStatus === 'active'" class="trip-status-badge status-active">旅行中</span>
          <span v-else-if="tripStatus === 'done'" class="trip-status-badge status-done">完了</span>
          <span v-else class="trip-status-badge status-planning">計画中</span>
        </div>
        <!-- 3行目: メタ情報 (日付・メンバー) -->
        <div class="trip-header-meta" style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 1rem; margin-top: 1rem; display: flex; gap: 1.5rem; flex-wrap: wrap;">
          <div class="trip-stats-row" style="display: flex; gap: 1.5rem; align-items: center; width: 100%;">
            <span class="trip-stat" style="display: flex; align-items: center; gap: 0.5rem; color: #cbd5e1;">
              <div class="member-avatars" style="display: flex; margin-right: 0.5rem;">
                <div v-for="(m, i) in members.slice(0, 5)" :key="m.MEMBER_CODE" class="avatar-circle" :style="{ zIndex: 5 - i, marginLeft: i > 0 ? '-10px' : '0' }">
                  <img v-if="m.PROFILE_IMAGE_BASE64" :src="m.PROFILE_IMAGE_BASE64" alt="avatar" style="width: 24px; height: 24px; border-radius: 50%; border: 2px solid #1e293b; object-fit: cover;" />
                  <div v-else style="width: 24px; height: 24px; border-radius: 50%; border: 2px solid #1e293b; background: #64748b; display: flex; align-items: center; justify-content: center;">
                    <IconSvg name="user-solid-full" size="xs" />
                  </div>
                </div>
                <div v-if="members.length > 5" style="width: 24px; height: 24px; border-radius: 50%; border: 2px solid #1e293b; background: #334155; color: white; display: flex; align-items: center; justify-content: center; font-size: 10px; z-index: 0; margin-left: -10px;">
                  +{{ members.length - 5 }}
                </div>
              </div>
              <span class="stat-value">{{ memberCount }}人</span>
            </span>
            <span class="trip-stat" v-if="shioriDates" style="display: flex; align-items: center; gap: 0.5rem; color: #cbd5e1;">
              <IconSvg name="calendar-solid-full" size="sm" />
              <span class="stat-value">{{ shioriDates }}</span>
            </span>
            <span class="trip-stat" style="display: flex; align-items: center; gap: 0.5rem; color: #cbd5e1;">
              <IconSvg name="yen-sign-solid-full" size="sm" />
              <span class="stat-value">{{ totalCostDisplay || '-' }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- タブナビゲーション -->
    <div class="tabs-wrapper" v-if="shioriData">
      <TabMenu
        :tabs="tabs"
        :active-tab="activeTab"
        @update:active-tab="switchTab"
      />
    </div>

    <!-- タブコンテンツ -->
    <div class="tab-content">
      <div class="container">
        <!-- 旅行概要タブ -->
        <div v-if="activeTab === 'overview'" class="overview-tab">
          <TripLivePanel
            v-if="isTripLiveVisible"
            :shiori-data="shioriData"
            :schedules="schedulesForBooking"
            @open-schedule="openLiveSchedule"
          />

          <section
            v-if="canWrite && reviewPromptForThisShiori && !hasTripReview"
            class="review-banner"
          >
            <div class="review-banner-main">
              <span class="review-banner-label">旅行の振り返り</span>
              <strong>この旅行を振り返りませんか</strong>
              <p>
                5指標評価とメモを、自分用の記録として残せます。
              </p>
            </div>
            <div class="review-banner-actions">
              <button
                class="review-banner-primary"
                type="button"
                @click="openTripReview"
              >
                振り返る
              </button>
              <button
                v-if="reviewPromptForThisShiori"
                class="review-banner-secondary"
                type="button"
                @click="snoozeReviewPrompt"
              >
                あとで
              </button>
              <button
                v-if="reviewPromptForThisShiori"
                class="review-banner-secondary"
                type="button"
                @click="dismissReviewPrompt"
              >
                閉じる
              </button>
            </div>
          </section>

          <section
            v-if="canRead && hasTripReview"
            class="review-chart-summary"
          >
            <div class="review-chart-header">
              <div class="review-chart-copy">
                <div class="review-chart-label-row">
                  <div>
                    <span class="review-banner-label">5指標評価</span>
                    <h2>旅の評価バランス</h2>
                  </div>
                  <button
                    class="review-banner-secondary review-chart-edit-button"
                    type="button"
                    @click="openTripReview"
                  >
                    {{ canWrite ? "振り返りを編集" : "振り返りを見る" }}
                  </button>
                </div>
                <strong class="review-overall-score">{{ reviewOverallScoreText }}</strong>
                <p>充実さ・時間効率・コスパ・快適性・新鮮さのバランスです。</p>
              </div>
            </div>
            <div class="review-radar" aria-label="旅行評価レーダーチャート">
              <svg viewBox="-32 -6 284 214" role="img">
                <polygon
                  v-for="level in radarLevels"
                  :key="level"
                  :points="radarPolygon(level)"
                  class="review-radar-grid"
                />
                <line
                  v-for="axis in radarAxes"
                  :key="axis.key"
                  x1="110"
                  y1="110"
                  :x2="axis.x"
                  :y2="axis.y"
                  class="review-radar-axis"
                />
                <polygon
                  v-if="reviewRadarPoints"
                  :points="reviewRadarPoints"
                  class="review-radar-shape"
                />
                <circle
                  v-for="point in reviewRadarPointList"
                  :key="point.key"
                  :cx="point.x"
                  :cy="point.y"
                  r="4"
                  class="review-radar-dot"
                />
                <text
                  v-for="axis in radarAxes"
                  :key="`${axis.key}-label`"
                  :x="axis.labelX"
                  :y="axis.labelY"
                  class="review-radar-vertex-label"
                  :text-anchor="axis.textAnchor"
                  dominant-baseline="middle"
                >
                  <tspan
                    v-for="(line, lineIndex) in axis.labelLines"
                    :key="`${axis.key}-label-line-${lineIndex}`"
                    :x="axis.labelX"
                    :dy="lineIndex === 0 ? axis.labelDy : 12"
                  >
                    {{ line }}
                  </tspan>
                </text>
                <text
                  v-for="axis in radarAxes"
                  :key="`${axis.key}-score`"
                  :x="axis.scoreX"
                  :y="axis.scoreY"
                  class="review-radar-score-label"
                  text-anchor="middle"
                  dominant-baseline="middle"
                >
                  {{ axis.scoreText }}
                </text>
              </svg>
            </div>
          </section>

          <section
            v-if="canRead && ratedItemReviews.length"
            class="lodging-review-summary"
          >
            <div class="lodging-review-summary-header">
              <div>
                <span class="review-banner-label">予定ごとの評価</span>
                <h2>保存済みのアイテム評価</h2>
              </div>
              <button
                class="review-banner-secondary"
                type="button"
                @click="openTripReview"
              >
                {{ canWrite ? "振り返りを編集" : "振り返りを見る" }}
              </button>
            </div>
            <div class="review-category-filter" aria-label="評価カテゴリで絞り込み">
              <button
                v-for="filter in itemReviewFilters"
                :key="filter.key"
                type="button"
                class="review-filter-button"
                :class="{ active: activeItemReviewCategory === filter.key }"
                :aria-pressed="activeItemReviewCategory === filter.key"
                @click="activeItemReviewCategory = filter.key"
              >
                {{ filter.label }}
              </button>
            </div>
            <div
              v-if="filteredRatedItemReviews.length"
              class="lodging-review-summary-list"
            >
              <article
                v-for="item in filteredRatedItemReviews"
                :key="item.lodgingKey"
                class="lodging-review-summary-card"
              >
                <div class="lodging-review-card-copy">
                  <h3>{{ item.itemName || item.lodgingName || "評価対象" }}</h3>
                  <p>
                    {{ itemCategoryLabel(item.reviewCategory) }}
                    <span v-if="item.address || item.startDate">
                      ・{{ item.address || formatDate(item.startDate) }}
                    </span>
                  </p>
                  <div class="item-review-score-summary">
                    <strong>{{ item.overallScoreText }}</strong>
                  </div>
                </div>
                <div
                  class="review-radar item-review-radar"
                  :aria-label="`${item.itemName || item.lodgingName || '評価対象'}の評価レーダーチャート`"
                >
                  <svg viewBox="-32 -6 284 214" role="img">
                    <polygon
                      v-for="level in radarLevels"
                      :key="level"
                      :points="radarPolygon(level)"
                      class="review-radar-grid"
                    />
                    <line
                      v-for="axis in item.radarAxes"
                      :key="axis.key"
                      x1="110"
                      y1="110"
                      :x2="axis.x"
                      :y2="axis.y"
                      class="review-radar-axis"
                    />
                    <polygon
                      :points="item.radarPoints"
                      class="review-radar-shape"
                    />
                    <circle
                      v-for="point in item.radarPointList"
                      :key="point.key"
                      :cx="point.x"
                      :cy="point.y"
                      r="3.5"
                      class="review-radar-dot"
                    />
                    <text
                      v-for="axis in item.radarAxes"
                      :key="`${axis.key}-label`"
                      :x="axis.labelX"
                      :y="axis.labelY"
                      class="review-radar-vertex-label"
                      :text-anchor="axis.textAnchor"
                      dominant-baseline="middle"
                    >
                      <tspan
                        v-for="(line, lineIndex) in axis.labelLines"
                        :key="`${axis.key}-label-line-${lineIndex}`"
                        :x="axis.labelX"
                        :dy="lineIndex === 0 ? axis.labelDy : 12"
                      >
                        {{ line }}
                      </tspan>
                    </text>
                    <text
                      v-for="axis in item.radarAxes"
                      :key="`${axis.key}-score`"
                      :x="axis.scoreX"
                      :y="axis.scoreY"
                      class="review-radar-score-label"
                      text-anchor="middle"
                      dominant-baseline="middle"
                    >
                      {{ axis.scoreText }}
                    </text>
                  </svg>
                </div>
              </article>
            </div>
            <p v-else class="review-filter-empty">
              このカテゴリの保存済み評価はありません。
            </p>
          </section>

          <div class="card">
            <div class="card-header">
              <h2 class="card-title">旅行概要</h2>
              <div class="shiori-action-menu overview-action-menu" @click.stop>
                <button
                  class="overview-kebab-button"
                  type="button"
                  :aria-expanded="isActionMenuOpen"
                  aria-label="しおり操作メニュー"
                  @click="isActionMenuOpen = !isActionMenuOpen"
                >
                  ⋮
                </button>
                <div
                  v-if="isActionMenuOpen"
                  class="shiori-action-dropdown overview-action-dropdown"
                >
                  <button
                    v-if="canWrite"
                    class="shiori-action-item"
                    @click="runShioriAction(openEditModal)"
                  >
                    編集
                  </button>
                  <button
                    v-if="isAuthenticated && canManage"
                    class="shiori-action-item"
                    @click="runShioriAction(openShareModal)"
                  >
                    共有
                  </button>
                  <button
                    class="shiori-action-item"
                    @click="runShioriAction(copyShiori)"
                  >
                    複製
                  </button>
                  <button
                    v-if="isAuthenticated"
                    class="shiori-action-item"
                    :disabled="isExportingPdf"
                    @click="runShioriAction(saveShioriPdf)"
                  >
                    {{ isExportingPdf ? "PDF作成中..." : "PDF保存" }}
                  </button>
                  <button
                    v-if="isOwner"
                    class="shiori-action-item shiori-action-delete"
                    @click="runShioriAction(deleteShiori)"
                  >
                    しおりを削除
                  </button>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">しおりタイトル</span>
                  <span class="info-value">{{ shioriData?.SUBJECT_NMV }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">旅行日程</span>
                  <span class="info-value">
                    {{ formatDate(shioriData?.TRIP_FROM) }} ～
                    {{ formatDate(shioriData?.TRIP_TO) }}
                  </span>
                </div>
                <div class="info-item member-section">
                  <span class="info-label">メンバー</span>
                  <div class="members-list">
                    <div
                      v-for="member in members"
                      :key="member.MEMBER_CODE"
                      class="member-card"
                      :title="member.MEMBER_NAME"
                    >
                      <div class="member-avatar-large">
                        <img
                          v-if="member.PROFILE_IMAGE_BASE64"
                          :src="member.PROFILE_IMAGE_BASE64"
                          :alt="member.MEMBER_NAME"
                          class="avatar-img"
                          loading="lazy"
                          decoding="async"
                        />
                        <IconSvg
                          v-else
                          name="user-solid-full"
                          size="md"
                          class="avatar-placeholder"
                        />
                      </div>
                    </div>
                    <div v-if="members.length === 0" class="no-members">
                      メンバーが登録されていません
                    </div>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-label">渡航先</span>
                  <span class="info-value">
                    <span v-if="destinationDisplay.type === 'domestic'"
                      >JP {{ destinationDisplay.list }}</span
                    >
                    <span
                      v-else-if="destinationDisplay.type === 'international'"
                      ><IconSvg name="earth-americas-solid-full" size="xs" />
                      {{ destinationDisplay.list }}</span
                    >
                    <span v-else>-</span>
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">費用</span>
                  <span class="info-value">{{ totalCostDisplay }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">あなたの負担額</span>
                  <span class="info-value">{{ myCostDisplay }}</span>
                </div>
                <div v-if="shioriData?.BUDGET" class="info-item">
                  <span class="info-label">予算</span>
                  <span class="info-value budget-breakdown">
                    <span>1人あたり {{ budgetPerPersonDisplay }}</span>
                    <span>全体 {{ budgetTotalDisplay }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <TravelAdvice :shiori-data="shioriData" />
          <TravelReadinessPanel
            :schedules="schedulesForBooking"
            :checklist-items="checklistItemsForReadiness"
            @open-tab="switchTab"
          />
          <BookingAssistPanel
            :shiori-data="shioriData"
            :schedules="schedulesForBooking"
            :member-count="members.length"
          />
        </div>

        <!-- スケジュールタブ -->
        <div v-if="activeTab === 'schedule'">
          <ScheduleView :can-write="canWrite" />
        </div>

        <!-- 持ち物タブ -->
        <div v-if="activeTab === 'belongings'">
          <ChecklistView item-type="BELONGINGS" :can-write="canWrite" />
        </div>

        <!-- To Doタブ -->
        <div v-if="activeTab === 'todo'">
          <div v-if="todoNotificationMessage" class="todo-notification">
            <div class="notification-icon-wrap">
              <IconSvg name="triangle-exclamation-solid-full" size="sm" />
            </div>
            <p class="notification-text">{{ todoNotificationMessage }}</p>
          </div>
          <ChecklistView item-type="TODO" :can-write="canWrite" />
        </div>

        <!-- 割り勘タブ -->
        <div v-if="activeTab === 'warikan'">
          <WarikanView
            :read-only="!canWrite"
            @update:activeTab="activeTab = $event"
          />
        </div>

        <!-- 行きたいとこタブ -->
        <div v-if="activeTab === 'wishlist'">
          <WishlistView :can-write="canWrite" />
        </div>
      </div>
    </div>

    <!-- ローディング表示 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p>読み込み中...</p>
    </div>

    <!-- エラー表示 -->
    <div v-if="error" class="error-overlay">
      <div class="alert alert-error">
        <p>エラーが発生しました: {{ error }}</p>
        <button
          @click="$router.push('/mypage')"
          class="btn btn-secondary btn-sm mt-3"
        >
          マイページに戻る
        </button>
      </div>
    </div>

    <!-- しおり編集モーダル -->
    <ManageShioriModal
      v-if="shioriData"
      v-model="isEditModalOpen"
      :shiori-data="shioriData"
      :can-write="canWrite"
      :can-manage="canManage"
      :can-delete="isOwner"
      :can-delete-members="isOwner"
      @updated="handleShioriUpdated"
    />

    <!-- しおり複製モーダル -->
    <CopyShioriModal
      v-if="shioriData"
      v-model="isCopyModalOpen"
      :shiori-data="shioriData"
      @copied="handleShioriCopied"
    />

    <!-- しおり共有モーダル -->
    <ShareShioriModal
      v-if="shioriData"
      v-model="isShareModalOpen"
      :shiori-id="shioriData.SHIORI_NOV"
      :can-manage="canManage"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useShioriStore } from "@/store/shiori-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useChecklistStore } from "@/store/checklist-store.js";
import { useChecklistDetailStore } from "@/store/checklist-detail-store.js";
import { usechecklistDetailMemberAccountStore } from "@/store/checklist-detail-member-store.js";
import { useAuthStore } from "@/store/authStore.js";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useWarikanStore } from "@/store/warikan-store.js";
import { useWishlistStore } from "@/store/wishlist-store.js";
import { useTripReviewStore } from "@/store/trip-review-store.js";
import AppHeader from "@/components/AppHeader.vue";
import ScheduleView from "@/views/ScheduleView.vue";
import ChecklistView from "@/views/ChecklistView.vue";
import WarikanView from "@/views/WarikanView.vue";
import WishlistView from "@/views/WishlistView.vue";
import ManageShioriModal from "@/components/ManageShioriModal.vue";
import CopyShioriModal from "@/components/CopyShioriModal.vue";
import ShareShioriModal from "@/components/ShareShioriModal.vue";
import TabMenu from "@/components/TabMenu.vue";
import TravelAdvice from "@/components/TravelAdvice.vue";
import TravelReadinessPanel from "@/components/TravelReadinessPanel.vue";
import BookingAssistPanel from "@/components/BookingAssistPanel.vue";
import TripLivePanel from "@/components/TripLivePanel.vue";
import IconSvg from "@/components/IconSvg.vue";
import {
  fetchAuthenticatedShioriImageUrl,
  resolveEmbeddedShioriImageUrl,
  revokeShioriImageUrl,
} from "@/utils/shiori-image.js";
import { isTripActiveToday } from "@/utils/trip-live.js";
import { isTripVisited } from "@/utils/trip-status.js";
import { exportShioriPdf, openShioriPdfWindow } from "@/utils/shiori-pdf.js";

export default {
  name: "ShioriView",
  components: {
    AppHeader,
    TabMenu,
    ScheduleView,
    ChecklistView,
    WarikanView,
    WishlistView,
    ManageShioriModal,
    CopyShioriModal,
    ShareShioriModal,
    TravelAdvice,
    TravelReadinessPanel,
    BookingAssistPanel,
    TripLivePanel,
    IconSvg,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const shioriStore = useShioriStore();
    const shioriMemberStore = useShioriMemberStore();
    const checklistStore = useChecklistStore();
    const checklistDetailStore = useChecklistDetailStore();
    const checklistDetailMemberStore =
      usechecklistDetailMemberAccountStore();
    const authStore = useAuthStore();
    const scheduleStore = useScheduleStore();
    const warikanStore = useWarikanStore();
    const wishlistStore = useWishlistStore();
    const tripReviewStore = useTripReviewStore();
    const VALID_TABS = new Set([
      "overview",
      "schedule",
      "belongings",
      "todo",
      "warikan",
      "wishlist",
      "checklist",
    ]);
    const resolveTab = (tab) => {
      if (tab === "checklist") return "todo";
      return typeof tab === "string" && VALID_TABS.has(tab) ? tab : "overview";
    };
    const activeTab = ref(
      resolveTab(route.query.tab || route.meta.initialTab),
    );
    const isLoading = ref(true);
    const error = ref(null);
    const isEditModalOpen = ref(false);
    const isCopyModalOpen = ref(false);
    const isShareModalOpen = ref(false);
    const isExportingPdf = ref(false);
    const isActionMenuOpen = ref(false);
    const members = ref([]);
    const activeItemReviewCategory = ref("all");

    const tabs = [
      { id: "overview", label: "旅行概要", icon: "list-solid-full" },
      { id: "schedule", label: "スケジュール", icon: "calendar-solid-full" },
      { id: "warikan", label: "割り勘", icon: "yen-sign-solid-full" },
      { id: "belongings", label: "持ち物", icon: "bag-shopping-solid-full" },
      { id: "todo", label: "To Do", icon: "check-solid-full" },
      { id: "wishlist", label: "行きたいとこ", icon: "map-solid-full" },
    ];

    const reviewScoreLabels = [
      { key: "lodgingScore", label: "充実さ" },
      { key: "foodScore", label: "時間効率" },
      { key: "transportScore", label: "コスパ" },
      { key: "sightseeingScore", label: "快適性" },
      { key: "activityScore", label: "新鮮さ" },
    ];

    const itemCategoryLabels = {
      lodging: "宿泊",
      food: "食事",
      transport: "移動",
      sightseeing: "観光",
      activity: "アクティビティ",
    };

    const itemReviewFilters = [
      { key: "all", label: "すべて" },
      { key: "lodging", label: "宿泊" },
      { key: "food", label: "食事" },
      { key: "transport", label: "移動" },
      { key: "sightseeing", label: "観光" },
      { key: "activity", label: "アクティビティ" },
    ];

    const itemScoreLabelsByCategory = {
      lodging: ["清潔さ", "設備・アメニティ", "サービスの質", "立地・アクセス", "コストパフォーマンス"],
      food: ["味", "雰囲気", "サービス", "立地・アクセス", "コストパフォーマンス"],
      transport: ["快適さ", "時間の正確さ", "わかりやすさ", "立地・アクセス", "コストパフォーマンス"],
      sightseeing: ["見応え", "混雑具合", "アクセス", "滞在しやすさ", "コストパフォーマンス"],
      activity: ["楽しさ", "安全性", "スタッフ対応", "アクセス", "コストパフォーマンス"],
    };

    const itemScoreKeys = [
      "cleanlinessScore",
      "facilitiesScore",
      "serviceScore",
      "locationScore",
      "valueScore",
    ];

    const shioriId = computed(() => route.params.id);

    const shioriData = computed(() => {
      return (
        shioriStore.shiori.find((item) => item.SHIORI_NOV === shioriId.value) ||
        shioriStore.sharedShiori.find(
          (item) => item.SHIORI_NOV === shioriId.value,
        )
      );
    });

    const headerImageSource = computed(() => {
      const embeddedImageUrl = resolveEmbeddedShioriImageUrl(shioriData.value);
      if (embeddedImageUrl) return { embeddedImageUrl };
      if (shioriData.value?.HAS_IMAGE && shioriId.value) {
        return { shioriId: shioriId.value };
      }
      return null;
    });
    const loadedHeaderImageUrl = ref("");

    watch(
      headerImageSource,
      async (source, _previousSource, onCleanup) => {
        loadedHeaderImageUrl.value = "";
        let active = true;
        let objectUrl = "";

        onCleanup(() => {
          active = false;
          revokeShioriImageUrl(objectUrl);
        });

        if (!source) return;
        if (source.embeddedImageUrl) {
          const image = new Image();
          image.onload = () => {
            if (active) loadedHeaderImageUrl.value = source.embeddedImageUrl;
          };
          image.onerror = () => {
            if (active) loadedHeaderImageUrl.value = "";
          };
          image.src = source.embeddedImageUrl;
          return;
        }

        try {
          objectUrl = await fetchAuthenticatedShioriImageUrl(source.shioriId);
          if (active) loadedHeaderImageUrl.value = objectUrl;
          else revokeShioriImageUrl(objectUrl);
        } catch {
          if (active) loadedHeaderImageUrl.value = "";
        }
      },
      { immediate: true },
    );

    const access = computed(
      () =>
        shioriStore.accessByShioriId[shioriId.value] || {
          role: "UNKNOWN",
          canRead: false,
          canWrite: false,
          canManage: false,
          isOwner: false,
        },
    );
    const canRead = computed(() => access.value.canRead);
    const canWrite = computed(() => access.value.canWrite);
    const canManage = computed(() => access.value.canManage);
    const isOwner = computed(() => access.value.isOwner);
    const isReadOnly = computed(
      () => canRead.value && !canWrite.value,
    );

    const headerStyle = computed(() => {
      if (!loadedHeaderImageUrl.value) {
        return {};
      }
      return {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${loadedHeaderImageUrl.value})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    });

    const darkHeaderOverrideStyle = computed(() => {
      if (!loadedHeaderImageUrl.value) {
        return {};
      }
      return {
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.72), rgba(15, 23, 42, 0.85)), url(${loadedHeaderImageUrl.value})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    });

    const memberCount = computed(() => members.value.length);
    const displayMembers = computed(() => {
      return members.value.slice(0, 3).map(m => ({
        id: m.MEMBER_CODE,
        name: m.MEMBER_NAME,
        profileImageUrl: m.PROFILE_IMAGE_BASE64
      }));
    });
    const extraMembersCount = computed(() => Math.max(0, memberCount.value - 3));
    const shioriDates = computed(() => {
      if (!shioriData.value) return "";
      const from = formatDate(shioriData.value.TRIP_FROM);
      const to = formatDate(shioriData.value.TRIP_TO);
      if (from && to) return `${from} ～ ${to}`;
      return from || to || "";
    });
    const tripStatus = computed(() => {
      if (!shioriData.value) return "planning";
      const now = new Date();
      const from = new Date(shioriData.value.TRIP_FROM);
      if (shioriData.value.TRIP_TO) {
        const to = new Date(shioriData.value.TRIP_TO);
        to.setHours(23, 59, 59, 999);
        if (now < from) return "planning";
        if (now > to) return "done";
        return "active";
      }
      return "planning";
    });

    const memberDisplay = computed(() => {
      if (members.value.length === 0) {
        return "0名";
      }
      const memberNames = members.value.map((m) => m.MEMBER_NAME).join(", ");
      return `${memberNames} (${members.value.length}名)`;
    });

    const destinationDisplay = computed(() => {
      if (!shioriData.value) {
        return { type: null, list: "-" };
      }

      const type = shioriData.value.DESTINATION_TYPE || "domestic";
      const destinationsJson = shioriData.value.DESTINATIONS;

      if (!destinationsJson) {
        return { type, list: "-" };
      }

      try {
        const destinations = JSON.parse(destinationsJson);
        if (Array.isArray(destinations) && destinations.length > 0) {
          return {
            type,
            list: destinations.join("、"),
          };
        }
      } catch (e) {
        console.error("渡航先データのパースエラー:", e);
      }

      return { type, list: "-" };
    });

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      const weekday = weekdays[date.getDay()];
      return `${year}年${String(month).padStart(2, "0")}月${String(
        day,
      ).padStart(2, "0")}日(${weekday})`;
    };

    const formatTotalCost = (shiori) => {
      if (!shiori || !shiori.TOTAL_COST) return "-";

      const costs = shiori.TOTAL_COST;
      const currencies = Object.keys(costs);

      if (currencies.length === 0) return "-";

      // 通貨記号マップ
      const currencySymbols = {
        JPY: "¥",
        USD: "$",
        EUR: "€",
        GBP: "£",
        CNY: "¥",
        KRW: "₩",
      };

      // 複数通貨の場合は " + " で結合
      const formattedCosts = currencies.map((currency) => {
        const amount = costs[currency];
        const symbol = currencySymbols[currency] || currency;
        return `${symbol}${amount.toLocaleString()}`;
      });

      return formattedCosts.join(" + ");
    };

    const totalCostDisplay = computed(() => formatTotalCost(shioriData.value));
    const schedulesForBooking = computed(() => scheduleStore.schedules || []);
    const checklistItemsForReadiness = computed(
      () => checklistStore.checklists[shioriId.value] || [],
    );
    const isAuthenticated = computed(() => Boolean(authStore.token));

    const formatYen = (amount) => {
      const value = Number(amount || 0);
      if (!value) return "-";
      return `¥${Math.round(value).toLocaleString()}`;
    };

    const budgetMemberCount = computed(() => Math.max(members.value.length, 1));

    const budgetTotalDisplay = computed(() =>
      formatYen(shioriData.value?.BUDGET),
    );

    const budgetPerPersonDisplay = computed(() => {
      const budget = Number(shioriData.value?.BUDGET || 0);
      if (!budget) return "-";
      return formatYen(budget / budgetMemberCount.value);
    });

    const liveNow = ref(new Date());
    const isTripLiveVisible = computed(() =>
      isTripActiveToday(shioriData.value, liveNow.value),
    );
    const isTripEnded = computed(() => isTripVisited(shioriData.value));

    const reviewPromptForThisShiori = computed(() =>
      isTripEnded.value
        ? tripReviewStore.promptCandidates.find(
            (prompt) => prompt.shioriId === shioriId.value,
          )
        : null,
    );

    const hasTripReview = computed(
      () => isTripEnded.value && Boolean(tripReviewStore.currentReview),
    );

    const radarLevels = [1, 2, 3, 4, 5];
    const radarPoint = (index, value) => {
      const angle = -Math.PI / 2 + (index * 2 * Math.PI) / reviewScoreLabels.length;
      const radius = 82 * (Number(value || 0) / 5);
      return {
        x: 110 + Math.cos(angle) * radius,
        y: 110 + Math.sin(angle) * radius,
      };
    };

    const radarLabelPoint = (index) => {
      const angle = -Math.PI / 2 + (index * 2 * Math.PI) / reviewScoreLabels.length;
      const radius = 98;
      return {
        labelX: 110 + Math.cos(angle) * radius,
        labelY: 110 + Math.sin(angle) * radius,
      };
    };

    const radarScorePoint = (index) => {
      const angle = -Math.PI / 2 + (index * 2 * Math.PI) / reviewScoreLabels.length;
      const radius = 69;
      return {
        scoreX: 110 + Math.cos(angle) * radius,
        scoreY: 110 + Math.sin(angle) * radius,
      };
    };

    const radarTextAnchor = (index) => {
      if (index === 1 || index === 2) return "start";
      if (index === 3 || index === 4) return "end";
      return "middle";
    };

    const formatScoreText = (value) =>
      value === null || value === undefined ? "-/5" : `${value}/5`;

    const reviewCategoryScores = computed(() => {
      const review = tripReviewStore.currentReview || {};
      return reviewScoreLabels
        .map((item) => review[item.key])
        .filter((value) => value !== null && value !== undefined);
    });

    const reviewOverallScoreText = computed(() => {
      if (!reviewCategoryScores.value.length) {
        return "-/5";
      }
      const total = reviewCategoryScores.value.reduce(
        (sum, value) => sum + Number(value),
        0,
      );
      return `${(total / reviewCategoryScores.value.length).toFixed(1)}/5`;
    });

    const radarLabelLines = (label) => {
      if (label === "アクティビティ") return ["アクティ", "ビティ"];
      if (label === "コストパフォーマンス") return ["コスト", "パフォーマンス"];
      if (label.length > 6) {
        const splitIndex = Math.ceil(label.length / 2);
        return [label.slice(0, splitIndex), label.slice(splitIndex)];
      }
      return [label];
    };

    const radarLabelDy = (label) => {
      const lineCount = radarLabelLines(label).length;
      if (lineCount >= 3) return -12;
      return lineCount > 1 ? -6 : 0;
    };

    const radarPolygon = (level) => {
      return reviewScoreLabels
        .map((_, index) => {
          const point = radarPoint(index, level);
          return `${point.x},${point.y}`;
        })
        .join(" ");
    };

    const radarAxes = computed(() => {
      const review = tripReviewStore.currentReview || {};
      return reviewScoreLabels.map((item, index) => {
        const axisEnd = radarPoint(index, 5);
        return {
          ...item,
          value: review[item.key] ?? null,
          scoreText: formatScoreText(review[item.key]),
          textAnchor: radarTextAnchor(index),
          labelLines: radarLabelLines(item.label),
          labelDy: radarLabelDy(item.label),
          x: axisEnd.x,
          y: axisEnd.y,
          ...radarLabelPoint(index),
          ...radarScorePoint(index),
        };
      });
    });

    const reviewRadarPointList = computed(() =>
      radarAxes.value.map((item, index) => ({
        ...item,
        ...radarPoint(index, item.value || 0),
      })),
    );

    const reviewRadarPoints = computed(() =>
      reviewRadarPointList.value.map((point) => `${point.x},${point.y}`).join(" "),
    );

    const itemCategoryLabel = (category) =>
      itemCategoryLabels[category || "lodging"] || "予定";

    const itemScoreLabels = (category) => {
      const labels = itemScoreLabelsByCategory[category] || itemScoreLabelsByCategory.lodging;
      return itemScoreKeys.map((key, index) => ({ key, label: labels[index] }));
    };

    const compactScoreLabel = (label) => {
      if (label.includes("コスト")) return "コスパ";
      if (label.includes("設備")) return "設備";
      if (label.includes("サービス")) return "サービス";
      if (label.includes("立地")) return "立地";
      if (label.includes("アクセス")) return "アクセス";
      if (label.includes("スタッフ")) return "スタッフ";
      return label;
    };

    const itemRadarAxes = (item) =>
      itemScoreLabels(item.reviewCategory).map((score, index) => {
        const axisEnd = radarPoint(index, 5);
        return {
          ...score,
          shortLabel: compactScoreLabel(score.label),
          value: item[score.key] ?? null,
          scoreText: formatScoreText(item[score.key]),
          textAnchor: radarTextAnchor(index),
          labelLines: radarLabelLines(compactScoreLabel(score.label)),
          labelDy: radarLabelDy(compactScoreLabel(score.label)),
          x: axisEnd.x,
          y: axisEnd.y,
          ...radarLabelPoint(index),
          ...radarScorePoint(index),
        };
      });

    const itemRadarPointList = (item) =>
      itemRadarAxes(item).map((score, index) => ({
        ...score,
        ...radarPoint(index, score.value || 0),
      }));

    const itemOverallScoreText = (scores) => {
      if (!scores.length) return "未選択";
      const total = scores.reduce((sum, score) => sum + Number(score.value), 0);
      return `${(total / scores.length).toFixed(1)}/5`;
    };

    const ratedItemReviews = computed(() => {
      if (!isOwner.value) return [];
      return tripReviewStore.itemReviews
        .map((item) => {
          const radarAxesForItem = itemRadarAxes(item);
          const radarPointListForItem = itemRadarPointList(item);
          const scores = radarAxesForItem.filter(
            (score) => score.value !== null && score.value !== undefined,
          );
          return {
            ...item,
            radarAxes: radarAxesForItem,
            radarPointList: radarPointListForItem,
            radarPoints: radarPointListForItem
              .map((point) => `${point.x},${point.y}`)
              .join(" "),
            scores,
            overallScoreText: itemOverallScoreText(scores),
          };
        })
        .filter((item) => item.scores.length > 0);
    });

    const filteredRatedItemReviews = computed(() => {
      if (activeItemReviewCategory.value === "all") {
        return ratedItemReviews.value;
      }
      return ratedItemReviews.value.filter(
        (item) =>
          (item.reviewCategory || "lodging") === activeItemReviewCategory.value,
      );
    });

    const myCostDisplay = computed(() => {
      const currentUserCode =
        authStore.user?.APP_USER_CODE || authStore.user?.user_id;
      const myMember = members.value.find(
        (member) => member.APP_USER_CODE === currentUserCode,
      );
      if (!myMember) return "-";

      const totals = {};
      const addCost = (currency, amount) => {
        const code = currency || "JPY";
        totals[code] = (totals[code] || 0) + amount;
      };

      scheduleStore.schedules.forEach((item) => {
        const participants =
          item.MEMBERS?.filter(
            (member) => member.SPLIT_PARTICIPATE_FLG === "1",
          ) || [];
        if (
          Number(item.COST) > 0 &&
          participants.some(
            (member) => member.MEMBER_CODE === myMember.MEMBER_CODE,
          )
        ) {
          addCost(item.CURRENCY, Number(item.COST) / participants.length);
        }
      });

      warikanStore.warikans
        .filter((item) => !item.SCHEDULE_LINE_NON)
        .forEach((item) => {
          const participants = warikanStore.warikanMembers.filter(
            (member) => member.LINE_NON === item.LINE_NON,
          );
          if (
            Number(item.AMOUNT) > 0 &&
            participants.some(
              (member) =>
                member.BENEFICIARY_USER_CODE === myMember.MEMBER_CODE,
            )
          ) {
            addCost(item.CURRENCY, Number(item.AMOUNT) / participants.length);
          }
        });

      const entries = Object.entries(totals);
      if (entries.length === 0) return "-";
      return entries
        .map(
          ([currency, amount]) =>
            `${currency === "JPY" ? "¥" : `${currency} `}${Math.round(
              amount,
            ).toLocaleString()}`,
        )
        .join(" + ");
    });

    // To Doの期限通知メッセージを生成
    const todoNotificationMessage = computed(() => {
      const items = checklistStore.checklists[shioriId.value] || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const threeDaysLater = new Date(today);
      threeDaysLater.setDate(threeDaysLater.getDate() + 3);

      let urgentCount = 0;
      let expiredCount = 0;

      items.forEach((item) => {
        if (
          item.ITEM_TYPE === "TODO" &&
          item.COMPLETED_FLG === "0" &&
          item.DEADLINE
        ) {
          const deadline = new Date(item.DEADLINE);
          deadline.setHours(0, 0, 0, 0);
          if (deadline < today) {
            expiredCount++;
          } else if (deadline <= threeDaysLater) {
            urgentCount++;
          }
        }
      });

      if (urgentCount === 0 && expiredCount === 0) return "";
      let msg = "";
      if (expiredCount > 0) msg += `期限切れが ${expiredCount} 件`;
      if (urgentCount > 0)
        msg += (msg ? "、" : "") + `期限間近が ${urgentCount} 件`;
      return msg + "あります。確認してください。";
    });

    const openEditModal = () => {
      if (!canWrite.value) return;
      isEditModalOpen.value = true;
    };

    const handleShioriUpdated = async () => {
      // しおりデータを再取得して画面を更新
      await shioriStore.fetchShiori({ force: true });

      // メンバー情報も再取得
      if (shioriId.value) {
        try {
          members.value = await shioriMemberStore.fetchMembers(shioriId.value);
        } catch (error) {
          console.error("メンバー再取得エラー:", error);
        }
      }

      isEditModalOpen.value = false;
    };

    const handleShioriCopied = async () => {
      // 複製後は何もしない（モーダル内で遷移する）
      isCopyModalOpen.value = false;
    };

    const copyShiori = () => {
      // 複製用のモーダルを開く
      isCopyModalOpen.value = true;
    };

    const openShareModal = () => {
      if (!canManage.value) return;
      // 共有モーダルを開く
      isShareModalOpen.value = true;
    };

    const saveShioriPdf = async () => {
      if (!shioriId.value) {
        return;
      }

      let printWindow = null;

      try {
        isExportingPdf.value = true;
        printWindow = openShioriPdfWindow("PDFを準備中");
        const payload = await shioriStore.getShioriExportData(shioriId.value);
        await exportShioriPdf(payload, printWindow);
      } catch (exportError) {
        console.error("PDF出力エラー:", exportError);
        if (printWindow && !printWindow.closed) {
          printWindow.close();
        }
        alert(
          exportError?.response?.data?.detail ||
            exportError?.message ||
            "PDFの作成に失敗しました",
        );
      } finally {
        isExportingPdf.value = false;
      }
    };

    const deleteShiori = async () => {
      if (!isOwner.value) return;
      if (!confirm("このしおりを削除しますか？")) return;

      try {
        await shioriStore.deleteShiori(shioriId.value);
        alert("しおりをゴミ箱に移動しました");
        router.push("/mypage");
      } catch (err) {
        console.error("しおり削除エラー:", err);
        alert("しおりの削除に失敗しました");
      }
    };

    const closeActionMenu = () => {
      isActionMenuOpen.value = false;
    };

    const runShioriAction = (action) => {
      closeActionMenu();
      action();
    };

    const openTripReview = () => {
      if (!canRead.value) return;
      router.push(`/shiori/${shioriId.value}/review`);
    };

    const addDaysFromToday = (days) => {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
    };

    const dismissReviewPrompt = async () => {
      if (!canWrite.value) return;
      await tripReviewStore.updatePromptState(shioriId.value, {
        action: "dismiss",
      });
    };

    const snoozeReviewPrompt = async () => {
      if (!canWrite.value) return;
      await tripReviewStore.updatePromptState(shioriId.value, {
        action: "snooze",
        snoozeUntil: addDaysFromToday(7),
      });
    };

    const removeRevokedShiori = async () => {
      shioriStore.removeShioriFromMemory(shioriId.value);
      tripReviewStore.clearShioriReviewData(shioriId.value);
      scheduleStore.clearSchedules();
      checklistStore.clearShioriChecklists(shioriId.value);
      checklistDetailStore.clearShioriChecklistDetails(shioriId.value);
      checklistDetailMemberStore.clearShioriChecklistDetailMembers(
        shioriId.value,
      );
      warikanStore.clearWarikanData();
      wishlistStore.clearShioriWishlist(shioriId.value);
      shioriMemberStore.clearShioriMembers(shioriId.value);
      members.value = [];
      error.value = null;
      await router.replace("/mypage");
    };

    const refreshAccessAfterDenial = async (event) => {
      if (String(event.detail?.shioriId) !== String(shioriId.value)) return;
      try {
        const refreshed = await shioriStore.fetchShioriAccess(shioriId.value, {
          force: true,
        });
        if (!refreshed.canWrite) {
          isEditModalOpen.value = false;
        }
        if (!refreshed.canManage) {
          isShareModalOpen.value = false;
        }
        closeActionMenu();
      } catch (accessError) {
        if ([403, 404].includes(accessError.response?.status)) {
          await removeRevokedShiori();
        }
      }
    };

    onMounted(async () => {
      document.addEventListener("click", closeActionMenu);
      window.addEventListener(
        "rabiitrip:shiori-access-denied",
        refreshAccessAfterDenial,
      );
      try {
        isLoading.value = true;

        try {
          await shioriStore.fetchShioriAccess(shioriId.value, { force: true });
        } catch (accessError) {
          if ([403, 404].includes(accessError.response?.status)) {
            await removeRevokedShiori();
            return;
          }
          throw accessError;
        }

        await shioriStore.fetchShiori({ force: true });

        // 共有しおりも取得（自分のしおりに見つからない場合に備えて）
        if (!shioriData.value) {
          await shioriStore.fetchSharedWithMe();
        }

        // メンバー情報を取得
        if (shioriId.value) {
          try {
            tripReviewStore.resetCurrentReview();
            const baseFetches = [
              shioriMemberStore.fetchMembers(shioriId.value),
              scheduleStore.fetchSchedules(shioriId.value),
            ];
            if (isAuthenticated.value) {
              baseFetches.push(
                checklistStore.fetchChecklists(shioriId.value),
                warikanStore.fetchWarikans(shioriId.value),
                warikanStore.fetchWarikanMembers(shioriId.value),
                tripReviewStore.fetchReviewPrompts(),
                tripReviewStore.fetchReview(shioriId.value),
              );
            }
            const [fetchedMembers] = await Promise.all(baseFetches);
            members.value = fetchedMembers;
          } catch (memberError) {
            console.error("メンバー取得エラー:", memberError);
            members.value = [];
          }
        }

        // データ取得後もしおりが見つからない場合
        if (!shioriData.value) {
          error.value = "しおりが見つかりませんでした";
        }
      } catch (err) {
        console.error("しおり取得エラー:", err);
        error.value = "しおりの読み込みに失敗しました";
      } finally {
        isLoading.value = false;
      }
    });

    onUnmounted(() => {
      document.removeEventListener("click", closeActionMenu);
      window.removeEventListener(
        "rabiitrip:shiori-access-denied",
        refreshAccessAfterDenial,
      );
    });

    // タブ切り替え: URLも同期させてリセット問題を防止
    const switchTab = (tabId) => {
      activeTab.value = tabId;
      router.replace({ query: { tab: tabId } });
    };

    const openLiveSchedule = (scheduleLineNon) => {
      activeTab.value = "schedule";
      router.replace({
        query: {
          tab: "schedule",
          ...(scheduleLineNon ? { schedule: scheduleLineNon } : {}),
        },
      });
    };

    // クエリパラメータの変更を監視してタブを切り替え
    watch(
      [() => route.query.tab, () => route.meta.initialTab],
      ([newTab, initialTab]) => {
        const resolved = resolveTab(newTab || initialTab);
        if (resolved !== activeTab.value) {
          activeTab.value = resolved;
        }
      },
      { immediate: true },
    );

    return {
      activeTab,
      switchTab,
      tabs,
      shioriData,
      isTripLiveVisible,
      reviewPromptForThisShiori,
      hasTripReview,
      reviewOverallScoreText,
      ratedItemReviews,
      filteredRatedItemReviews,
      activeItemReviewCategory,
      itemReviewFilters,
      radarLevels,
      radarAxes,
      radarPolygon,
      reviewRadarPoints,
      reviewRadarPointList,
      itemCategoryLabel,
      isAuthenticated,
      canRead,
      canWrite,
      canManage,
      isOwner,
      isReadOnly,
      headerStyle,
      darkHeaderOverrideStyle,
      displayMembers,
      extraMembersCount,
      memberCount,
      shioriDates,
      tripStatus,
      members,
      todoNotificationMessage,
      schedulesForBooking,
      checklistItemsForReadiness,
      memberDisplay,
      destinationDisplay,
      isLoading,
      error,
      isEditModalOpen,
      isCopyModalOpen,
      isShareModalOpen,
      isExportingPdf,
      isActionMenuOpen,
      formatDate,
      formatTotalCost,
      totalCostDisplay,
      budgetPerPersonDisplay,
      budgetTotalDisplay,
      myCostDisplay,
      openEditModal,
      handleShioriUpdated,
      handleShioriCopied,
      copyShiori,
      openShareModal,
      saveShioriPdf,
      deleteShiori,
      openLiveSchedule,
      runShioriAction,
      openTripReview,
      dismissReviewPrompt,
      snoozeReviewPrompt,
    };
  },
};
</script>

<style scoped>
.shiori-view {
  min-height: 100vh;
  background-color: var(--color-background);
  /* 一括操作バー等の sticky 位置（AppHeader + TabMenu） */
  --sticky-app-header: 57px;
  --sticky-tab-menu: 49px;
}

/* しおりヘッダー */
.shiori-header.trip-dark-header {
  background: var(--trip-header-bg);
  color: var(--trip-header-fg);
  padding: 1.25rem 0;
  min-height: auto;
  display: block;
}

.shiori-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.shiori-action-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  min-width: 160px;
  z-index: 1000;
  overflow: hidden;
}

.shiori-action-item {
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.shiori-action-item:last-child {
  border-bottom: none;
}

.shiori-action-item:hover:not(:disabled) {
  background-color: var(--color-background);
}

.shiori-action-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.shiori-action-delete {
  color: var(--color-danger);
}

.shiori-action-delete:hover {
  background-color: var(--color-danger-light);
}

.shiori-action-menu {
  position: relative;
  display: inline-block;
}

.overview-action-menu {
  display: none;
}

.overview-kebab-button {
  width: 2.25rem;
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    transform var(--transition-fast);
}

.overview-kebab-button:hover {
  background: rgba(255, 255, 255, 0.32);
  transform: translateY(-1px);
}

.overview-action-dropdown {
  top: calc(100% + 0.25rem);
}

@media (max-width: 767px) {
  .shiori-header .header-actions {
    display: none !important;
  }

  .shiori-header-top {
    margin-bottom: 0.75rem;
  }

  .shiori-header-top .back-link {
    margin-bottom: 0;
  }
}

.shiori-header .container {
  text-align: left;
  width: 100%;
}

/* 背景画像がある場合のオーバーレイ効果は inline style で実装済み */

.back-link {
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  transition: opacity var(--transition-fast);
}

.back-link:hover {
  opacity: 0.8;
}

.read-only-badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  margin: 0.5rem 0;
  padding: 0.25rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: var(--radius-full);
  background: rgba(15, 39, 66, 0.72);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.shiori-title {
  font-size: 1.25rem; /* mobile */
  font-weight: 700;
  margin-bottom: 0.75rem; /* mobile */
  color: white;
  text-align: left;
}

.shiori-meta {
  display: flex;
  flex-direction: column; /* mobile */
  gap: 1rem;
  font-size: 0.8125rem; /* mobile */
  color: rgba(255, 255, 255, 0.9);
  align-items: flex-start; /* mobile */
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* タブラッパー */
.tabs-wrapper {
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border-light);
  position: relative; /* mobile: not sticky */
  top: 0;
  z-index: 95;
}

/* タブコンテンツ */
.tab-content {
  padding: 1rem 0.75rem; /* mobile */
}

/* 概要タブ */
.overview-tab {
  max-width: 100%; /* mobile */
  margin: 0 auto;
  --review-navy: #EEF4FA;
  --review-navy-dark: #0F2742;
  --review-navy-soft: #F6FAFE;
  --review-navy-line: #B8C7D8;
}

/* カードヘッダーをグリーンに */
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: linear-gradient(
    135deg,
    var(--shiori-light, #b4cf9e) 0%,
    var(--shiori-lighter, #c5dcb3) 100%
  );
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  margin: calc(-1 * var(--spacing-lg));
  margin-bottom: var(--spacing-lg);
  padding: 1rem var(--spacing-lg);
  border-bottom: none;
}

.card-title {
  color: white;
  margin: 0;
}

.info-grid {
  display: grid;
  gap: 1rem; /* mobile */
}

.info-item {
  display: grid;
  grid-template-columns: 1fr; /* mobile: stack vertically */
  gap: 0.375rem; /* mobile */
  align-items: start;
}

.info-item.member-section {
  align-items: start;
}

.members-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.member-card {
  display: flex;
  padding: 0;
  background: transparent;
  border-radius: 50%;
  border: none;
  transition: all 0.2s;
}

.member-card:hover {
  transform: translateY(-1px);
}

.member-avatar-large {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.member-avatar-large .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar-large .avatar-placeholder {
  color: var(--color-text-muted);
}

.no-members {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.info-label {
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.info-value {
  color: var(--color-text);
  font-size: 0.9375rem;
}

.budget-breakdown {
  display: grid;
  gap: 0.2rem;
}

.shiori-action-menu {
  position: relative;
}

.shiori-kebab-button {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--color-surface);
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: var(--radius-md);
  color: var(--primary-hover);
  font-size: 1.375rem;
  font-weight: 700;
  cursor: pointer;
}

.shiori-kebab-button:hover {
  background: var(--color-background);
}

.shiori-action-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 50;
  min-width: 170px;
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.shiori-action-item {
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: var(--color-surface);
  border: none;
  color: var(--color-text);
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
}

.shiori-action-item:hover:not(:disabled) {
  background: var(--color-background);
}

.shiori-action-item:disabled {
  opacity: 0.5;
  cursor: wait;
}

.shiori-action-delete {
  color: var(--color-danger);
}

/* To Do 期限通知バナー */
.todo-notification {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  color: #856404;
}
.notification-icon-wrap {
  flex-shrink: 0;
}
.notification-text {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
}

.review-banner {
  display: grid;
  gap: 0.85rem;
  margin-bottom: 1rem;
  padding: 0.95rem;
  background: var(--color-surface);
  border: 1px solid var(--review-navy-line);
  border-left: 5px solid var(--review-navy-dark);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.review-banner-main {
  display: grid;
  gap: 0.2rem;
}

.review-banner-label {
  color: #0F2742;
  font-size: 0.75rem;
  font-weight: 800;
}

.review-banner-main strong {
  color: var(--color-text);
  font-size: 1rem;
}

.review-banner-main p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.review-banner-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.review-banner-primary,
.review-banner-secondary {
  min-height: 44px;
  padding: 0 0.85rem;
  border-radius: var(--radius-md);
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 800;
  cursor: pointer;
}

.review-banner-primary:focus-visible,
.review-banner-secondary:focus-visible {
  outline: 3px solid var(--review-navy-line);
  outline-offset: 3px;
}

.review-banner-primary {
  color: var(--review-navy-dark);
  background: var(--review-navy);
  border: 1px solid var(--review-navy-line);
}

.review-banner-primary:hover {
  color: white;
  background: var(--review-navy-dark);
}

.review-banner-secondary {
  color: var(--review-navy-dark);
  background: var(--color-surface);
  border: 1px solid var(--review-navy-line);
}

.review-banner-secondary:hover {
  color: var(--review-navy-dark);
  background: var(--review-navy-soft);
  border-color: var(--review-navy-dark);
}

.review-chart-summary {
  position: relative;
  display: grid;
  gap: 0.55rem;
  margin-bottom: 0.75rem;
  padding: 0.7rem 0.8rem;
  background: var(--color-surface);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.review-chart-header {
  min-width: 0;
}

.review-chart-label-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  margin-bottom: 0.2rem;
}

.review-chart-edit-button {
  flex: 0 0 auto;
  white-space: nowrap;
}

.review-chart-copy {
  min-width: 0;
}

.review-chart-copy p {
  margin: 0;
}

.review-chart-copy h2 {
  margin: 0.15rem 0 0;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.35;
}

.review-overall-score {
  display: inline-block;
  margin: 0.1rem 0 0;
  color: var(--review-navy-dark);
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.1;
}

.review-chart-copy p {
  margin-top: 0.12rem;
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.review-radar {
  display: grid;
  gap: 0;
  justify-items: center;
}

.review-radar svg {
  width: min(100%, 232px);
  aspect-ratio: 284 / 214;
}

.review-radar-grid {
  fill: rgba(15, 39, 66, 0.1);
  stroke: var(--review-navy-line);
  stroke-width: 1;
}

.review-radar-axis {
  stroke: var(--review-navy-line);
  stroke-width: 1;
}

.review-radar-shape {
  fill: rgba(15, 39, 66, 0.18);
  stroke: #0F2742;
  stroke-width: 2;
}

.review-radar-dot {
  fill: var(--review-navy-dark);
}

.review-radar-vertex-label {
  fill: var(--review-navy-dark);
  font-size: 11px;
  font-weight: 800;
  paint-order: stroke;
  stroke: white;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 4px;
}

.review-radar-score-label {
  fill: var(--review-navy-dark);
  font-size: 12px;
  font-weight: 800;
  paint-order: stroke;
  stroke: white;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 4px;
}

.lodging-review-summary {
  display: grid;
  gap: 0.7rem;
  margin-bottom: 0.75rem;
  padding: 0.8rem;
  background: var(--color-surface);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.lodging-review-summary-header {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
}

.lodging-review-summary-header h2 {
  margin: 0.15rem 0 0;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.35;
}

.lodging-review-summary-list {
  display: flex;
  gap: 0.75rem;
  margin: 0 -0.25rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding: 0 0.25rem 0.4rem;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
}

.lodging-review-summary-card {
  display: grid;
  flex: 0 0 min(86vw, 360px);
  gap: 0.45rem;
  padding: 0.7rem;
  background: var(--review-navy);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
  scroll-snap-align: start;
}

.lodging-review-card-copy {
  display: grid;
  gap: 0.45rem;
}

.lodging-review-summary-card h3 {
  margin: 0;
  color: var(--color-text);
  font-size: 0.95rem;
}

.lodging-review-summary-card p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.item-review-score-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.55rem;
  align-items: center;
  justify-self: start;
  padding: 0.25rem 0.55rem;
  color: #0F2742;
  background: var(--color-surface);
  border: 1px solid var(--review-navy-line);
  border-radius: 999px;
}

.item-review-score-summary strong,
.item-review-score-summary span {
  font-size: 0.82rem;
  font-weight: 800;
}

.review-category-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.review-filter-button {
  min-height: 40px;
  padding: 0 0.7rem;
  color: var(--review-navy-dark);
  background: var(--color-surface);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-full);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
}

.review-filter-button.active,
.review-filter-button:hover {
  color: var(--review-navy-dark);
  background: var(--review-navy);
  border-color: var(--review-navy-line);
}

.review-filter-button:focus-visible {
  outline: 3px solid var(--review-navy-line);
  outline-offset: 3px;
}

.item-review-radar {
  justify-items: stretch;
}

.item-review-radar svg {
  width: min(100%, 180px);
  justify-self: center;
}

.item-review-radar .review-radar-vertex-label {
  font-size: 10px;
}

.review-filter-empty {
  margin: 0;
  padding: 0.85rem;
  color: var(--review-navy-dark);
  background: var(--review-navy-soft);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  font-weight: 700;
}

/* ローディング・エラー表示 */
.loading-overlay,
.error-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1000;
}

.mt-3 {
  margin-top: 1rem;
}

/* ========================================
   レスポンシブ - モバイルファースト
   ======================================== */

@media (max-width: 767px) {
  .shiori-header {
    padding: 1.25rem 0.875rem;
    border-radius: 0 0 var(--radius-xl) var(--radius-xl);
  }

  .shiori-header-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .shiori-header-top .header-actions {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    width: 100%;
    gap: 0.4rem;
  }

  .shiori-header-top .btn-header-action {
    padding: 0.5rem 0.25rem;
    font-size: 0.8125rem;
    justify-content: center;
    text-align: center;
    width: 100%;
    min-width: 44px;
    min-height: 44px;
  }

  .overview-kebab-button,
  .shiori-action-item {
    min-width: 44px;
    min-height: 44px;
  }

  .header-actions {
    display: none;
  }

  .overview-action-menu {
    display: inline-block;
  }

  .shiori-title-row {
    margin: 0.5rem 0;
  }

  .shiori-title-row .trip-title {
    font-size: 1.35rem;
    line-height: 1.3;
  }

  .trip-stats-row {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.5rem !important;
  }

  .tab-content {
    padding: 1rem 0.75rem calc(5rem + env(safe-area-inset-bottom, 0));
  }

  .overview-tab > .card {
    padding: 0.875rem;
    border-radius: var(--radius-lg);
  }
}

/* タブレット (768px〜) */
@media (min-width: 768px) {
  .shiori-header {
    padding: 1.75rem 1rem;
    min-height: 180px;
  }

  .shiori-title {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }

  .shiori-meta {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem;
    font-size: 0.875rem;
    align-items: center;
  }

  .shiori-view {
    --sticky-app-header: 73px;
    --sticky-tab-menu: 53px;
  }

  .tabs-wrapper {
    position: sticky;
    top: var(--sticky-app-header);
  }

  .tab-content {
    padding: 1.5rem 1rem;
  }

  .info-item {
    grid-template-columns: 140px 1fr;
    gap: 0.75rem;
  }

  .info-grid {
    gap: 1.5rem;
  }

  .member-card {
    padding: 0;
  }

  .lodging-review-summary-card {
    flex-basis: min(72vw, 620px);
    grid-template-columns: minmax(0, 1fr) minmax(170px, 220px);
    align-items: center;
  }
}

/* デスクトップ (1024px〜) */
@media (min-width: 1024px) {
  .shiori-header {
    padding: 2.5rem 1rem;
    min-height: 200px;
  }

  .shiori-title {
    font-size: 2rem;
  }

  .shiori-meta {
    font-size: 0.9375rem;
  }

  .tab-content {
    padding: 2rem 1rem;
  }

  .info-item {
    grid-template-columns: 160px 1fr;
    gap: 1rem;
  }

  .overview-tab {
    max-width: 900px;
  }

}
</style>
